import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

type NestingNode = {
	type?: string;
	attrs?: {
		extensionKey?: string;
		extensionType?: string;
	};
};

const LCM_EXTENSION_KEY = 'legacy-content';
const LCM_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'inlineExtension',
	'bodiedExtension',
	'multiBodiedExtension',
]);

const isExtensionNode = (node: NestingNode): boolean =>
	typeof node.type === 'string' && EXTENSION_NODE_TYPES.has(node.type);

const isLcmNode = (node: NestingNode): boolean =>
	isExtensionNode(node) &&
	node.attrs?.extensionKey === LCM_EXTENSION_KEY &&
	node.attrs?.extensionType === LCM_EXTENSION_TYPE;

/**
 * Tracks LCM extension ancestry from the ordered node/depth stream emitted by
 * traverseWithNestedContentAndDepth. Ordinary ADF containers are depth-neutral and an LCM node
 * does not count itself.
 */
export function createLcmNestingLevelTracker(): {
	getMaxNestingLevel: () => number;
	visit: (node: ADFEntity, depth: number) => void;
} {
	const extensionDepthByAdfDepth: Array<number | undefined> = [];
	let maxNestingLevel = 0;

	return {
		getMaxNestingLevel: () => maxNestingLevel,
		visit: (node, depth) => {
			// Retain only the active ancestry path before recording this node.
			extensionDepthByAdfDepth.length = depth;
			const parentExtensionDepth = extensionDepthByAdfDepth[depth - 1];
			let extensionDepth = parentExtensionDepth;

			if (isLcmNode(node)) {
				if (parentExtensionDepth !== undefined) {
					maxNestingLevel = Math.max(maxNestingLevel, parentExtensionDepth + 1);
				}
				extensionDepth = 0;
			} else if (parentExtensionDepth !== undefined && isExtensionNode(node)) {
				extensionDepth = parentExtensionDepth + 1;
				maxNestingLevel = Math.max(maxNestingLevel, extensionDepth);
			}

			extensionDepthByAdfDepth[depth] = extensionDepth;
		},
	};
}

/**
 * Extends traverse from @atlaskit/adf-utils to also walk into nestedContent
 * found in extension parameters (e.g. legacy-content, column macro).
 *  "content": [
        {
          "type": "extension",
          "attrs": {
            "extensionKey": "legacy-content",
            "extensionType": "com.atlassian.confluence.migration",
            "parameters": {
              "reason": "A column macro in a rich body macro can't be created or edited in the new editor.",
              "nestedContent": {
                "type": "doc",
                "content": [
                  {
                    "type": "bodiedExtension",
                    "attrs": {
                      "extensionType": "com.atlassian.confluence.macro.core",
                      "extensionKey": "column"
                  }
                ]
              }
            }
          }
        }
      ]
    }
  }
]
 */
export function traverseWithNestedContentAndDepth(
	adf: ADFEntity,
	visitor: (node: ADFEntity, depth: number) => void,
	depthOffset: number = 0,
): void {
	traverse(adf, {
		any: (node, _parent, _index, depth) => {
			visitor(node, depthOffset + depth);

			const nestedContent = node?.attrs?.parameters?.nestedContent;
			if (nestedContent?.content) {
				traverseWithNestedContentAndDepth(
					nestedContent as ADFEntity,
					visitor,
					depthOffset + depth + 1,
				);
			}

			return node;
		},
	});
}
